import React, { useEffect, useState } from "react";
import { Form, Button, message, Input, Card, Image, Popconfirm } from "antd";
import { useParams } from "react-router-dom";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import FileUpload from "../../../Components/Uploader";
import {
  pushinArticleAttachment,
  pullArticleAttachment,
  getArticleByID,
} from "../action";
import { IArticle, IArticleImage } from "../../../types/Article";
import { CLOUD_URL } from "../../../lib/env";

type Props = {
  onAttachmentsChange: () => void;
};

interface FormData {
  alt: string;
  image: {
    fileuploaded: boolean;
    name: string;
    folder: string;
  };
}

const ManageAttachments = (props: Props) => {
  const { id } = useParams();
  const [form] = Form.useForm<FormData>();
  const [isLoading, setIsLoading] = useState(false);
  const [article, setArticle] = useState<IArticle | null>(null);
  const [uploadedFile, setUploadedFile] = useState<{
    fileuploaded: boolean;
    name: string;
    folder: string;
  } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchData = () => {
    if (!id) return;

    getArticleByID(id).then((data) => {
      if (!data) return;
      setArticle(data);
    });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleFileUploaded = (fileData: {
    fileuploaded: boolean;
    name: string;
    folder: string;
  }) => {
    setUploadedFile(fileData);
    message.success("File uploaded successfully!");
  };

  const handleAddAttachment = () => {
    form
      .validateFields()
      .then(async (values) => {
        if (!uploadedFile) {
          message.error("Please upload an image before submitting.");
          return;
        }

        setIsLoading(true);
        try {
          const response = await pushinArticleAttachment(
            id || "",
            uploadedFile.name,
            values.alt
          );
          if (response) {
            message.success("Attachment added successfully!");
            props.onAttachmentsChange();
            setShowAddForm(false);
            setUploadedFile(null);
            form.resetFields();
            fetchData();
          }
        } catch (error) {
          message.error("Failed to add attachment.");
        } finally {
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Validation failed:", error);
        message.error("Please correct the errors in the form.");
      });
  };

  const handleRemoveAttachment = async (attachmentPath: string) => {
    if (!id) return;

    setIsLoading(true);
    try {
      const response = await pullArticleAttachment(id, attachmentPath);
      if (response) {
        message.success("Attachment removed successfully!");
        props.onAttachmentsChange();
        fetchData();
      }
    } catch (error) {
      message.error("Failed to remove attachment.");
    } finally {
      setIsLoading(false);
    }
  };

  const cancelAddForm = () => {
    setShowAddForm(false);
    setUploadedFile(null);
    form.resetFields();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Article Attachments</h3>
        <div className="flex gap-2">
          {!showAddForm && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddForm(true)}
            >
              Add Attachment
            </Button>
          )}
        </div>
      </div>

      {/* Display existing attachments */}
      {article?.Attachments && article.Attachments.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {article.Attachments.map(
            (attachment: IArticleImage, index: number) => (
              <Card
                key={index}
                cover={
                  <Image
                    alt={attachment.Alt}
                    src={`${CLOUD_URL}/${attachment.FilePath}`}
                    className="h-48 object-cover"
                  />
                }
                size="small"
                actions={[
                  <Popconfirm
                    key="delete"
                    title="Delete Attachment"
                    description="Are you sure you want to delete this attachment?"
                    onConfirm={() => handleRemoveAttachment(attachment.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={isLoading}
                    >
                      Delete
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  title={`Attachment ${index + 1}`}
                  description={attachment.Alt || "No description"}
                />
              </Card>
            )
          )}
        </div>
      )}

      {/* Show message when no attachments */}
      {(!article?.Attachments || article.Attachments.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          <p>No attachments found for this article.</p>
        </div>
      )}

      {/* Add attachment form */}
      {showAddForm && (
        <Card title="Add New Attachment" className="mt-4">
          <Form form={form} layout="vertical">
            <Form.Item
              label="Alt Text"
              name="alt"
              rules={[{ required: true, message: "Please enter alt text" }]}
            >
              <Input placeholder="Enter image alt text" />
            </Form.Item>

            <Form.Item label="Image" required>
              <FileUpload
                onFileUploaded={handleFileUploaded}
                enableCrop={false}
              />
            </Form.Item>

            <Form.Item>
              <div className="flex gap-2">
                <Button
                  type="primary"
                  onClick={handleAddAttachment}
                  loading={isLoading && showAddForm}
                  disabled={!uploadedFile}
                >
                  {isLoading ? "Adding..." : "Add Attachment"}
                </Button>
                <Button onClick={cancelAddForm}>Cancel</Button>
              </div>
            </Form.Item>
          </Form>
        </Card>
      )}
    </div>
  );
};

export default ManageAttachments;
